import { Text, TextTypes, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Box, Pressable, ScrollView, Skeleton } from 'native-base';
import React, { useMemo } from 'react';

type CustomTextStyle = Partial<TextTypes.Props>;

export declare namespace TabSelectorTypes {
  export type TabsWithComponent = Record<string, { label: string; component: React.FC<any> }>;

  export interface Props<T extends TabsWithComponent> {
    selectedTab: keyof T;
    setSelectedTab: (tab: keyof T) => void;
    isLoading?: boolean;
    isMobile?: boolean;
    tabList: T;
  }
}

export const TabSelector = <T extends TabSelectorTypes.TabsWithComponent>({
  selectedTab,
  setSelectedTab,
  tabList: tabListProp,
  isLoading,
  isMobile,
}: TabSelectorTypes.Props<T>) => {
  const tabs = useMemo(() => {
    const mappedEntries = Object.entries(tabListProp);
    const selectedStyle: CustomTextStyle = isMobile
      ? { color: 'neutral.darkest', fontWeight: Typography['fontweight.bold'] }
      : { color: 'neutral.white', fontWeight: Typography['fontweight.bold'] };

    const normalStyle: CustomTextStyle = isMobile
      ? { color: 'neutral.medium', variant: 'semibold' }
      : { color: 'neutral.white' };

    const indicatorStyle = isMobile
      ? { borderColor: 'neutral.darkest' }
      : { borderColor: 'neutral.white' };

    const tabList = mappedEntries.map(([key, value]) => {
      if (isLoading) {
        return <Skeleton key={key} flex={1} borderRadius={BoxRadius.sm} />;
      }

      if (key === selectedTab) {
        return (
          <VStack space={Spacing['stack.quarck']} key={key}>
            <Pressable onPress={() => setSelectedTab(key)}>
              <Text
                variant="semibold"
                fontSize={{ base: Typography['fontsize.lg'], sm: Typography['fontsize.md'] }}
                {...selectedStyle}>
                {value.label}
              </Text>
            </Pressable>
            <Box borderBottomWidth={2} width="50%" {...indicatorStyle} />
          </VStack>
        );
      }

      return (
        <Pressable key={key} onPress={() => setSelectedTab(key)}>
          <Text
            fontSize={{ base: Typography['fontsize.lg'], sm: Typography['fontsize.md'] }}
            {...normalStyle}>
            {value.label}
          </Text>
        </Pressable>
      );
    });

    return tabList;
  }, [isLoading, selectedTab, isMobile, setSelectedTab]);

  const stack = (
    <HStack
      space={{ base: Spacing['stack.xxs'], sm: Spacing['stack.xs'] }}
      borderRadius={BoxRadius.sm}>
      {tabs}
    </HStack>
  );

  if (isMobile) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        width="100%"
        minHeight={Spacing['inset.md']}
        maxHeight={Spacing['inset.md']}>
        {stack}
      </ScrollView>
    );
  }

  return (
    <Box
      height="56px"
      borderRadius={BoxRadius.sm}
      backgroundColor={Color['brand.tertiary.principal']}
      justifyContent="center"
      paddingX={{ base: 0, sm: Spacing['inset.sm'] }}>
      {stack}
    </Box>
  );
};
